// Los catálogos de Gescom del perfil de compra, como los imprime la ficha (diseño M4 §2 de fct-ferias):
// la API compara y guarda ids de catálogo, y la pantalla muestra estas etiquetas. Son los de MapeoCliente.

/** GC_C_TRAMOS_FACT: los tramos de facturación, 1-8. */
export const TRAMOS: Record<number, string> = {
  1: '<150K',
  2: '150-300K',
  3: '300-500K',
  4: '500-700K',
  5: '700K-1M',
  6: '1-1,5M',
  7: '1,5-2M',
  8: '>2M',
};

/** Los plazos de compra, 1-4. */
export const PLAZOS: Record<number, string> = {
  1: 'Menos de 1 año',
  2: '1 a 2 años',
  3: 'Más de 2 años',
  4: 'Sin plazo',
};

/** GC_C_SUBREGION 1-52 (los códigos de provincia del INE), con la etiqueta que imprime la ficha. */
export const PROVINCIAS: Record<number, string> = {
  1: 'Araba',
  2: 'Albacete',
  3: 'Alicante',
  4: 'Almería',
  5: 'Ávila',
  6: 'Badajoz',
  7: 'Baleares',
  8: 'Barcelona',
  9: 'Burgos',
  10: 'Cáceres',
  11: 'Cádiz',
  12: 'Castellón',
  13: 'Ciudad Real',
  14: 'Córdoba',
  15: 'A Coruña',
  16: 'Cuenca',
  17: 'Girona',
  18: 'Granada',
  19: 'Guadalajara',
  20: 'Gipuzkoa',
  21: 'Huelva',
  22: 'Huesca',
  23: 'Jaén',
  24: 'León',
  25: 'Lleida',
  26: 'La Rioja',
  27: 'Lugo',
  28: 'Madrid',
  29: 'Málaga',
  30: 'Murcia',
  31: 'Navarra',
  32: 'Ourense',
  33: 'Asturias',
  34: 'Palencia',
  35: 'Las Palmas',
  36: 'Pontevedra',
  37: 'Salamanca',
  38: 'Sta. Cruz de Tenerife',
  39: 'Cantabria',
  40: 'Segovia',
  41: 'Sevilla',
  42: 'Soria',
  43: 'Tarragona',
  44: 'Teruel',
  45: 'Toledo',
  46: 'Valencia',
  47: 'Valladolid',
  48: 'Bizkaia',
  49: 'Zamora',
  50: 'Zaragoza',
  51: 'Ceuta',
  52: 'Melilla',
};

/** Las opciones de un catálogo para un selector, por orden de id. */
export function opciones(
  catalogo: Record<number, string>,
): { value: number; label: string }[] {
  return Object.entries(catalogo).map(([id, label]) => ({
    value: Number(id),
    label,
  }));
}

/** Unos ids como sus etiquetas, unidas: «Madrid, Toledo»; «—» si no hay ninguno. */
export function etiquetas(
  catalogo: Record<number, string>,
  ids: number[],
): string {
  return ids.length === 0
    ? '—'
    : ids.map((id) => catalogo[id] ?? `#${id}`).join(', ');
}

// Los plazos de la ficha como los escribe el contrato, a sus ids de catálogo.
const PLAZO_DE_LA_FICHA: Record<string, number> = {
  '<1 año': 1,
  '1-2 años': 2,
  '>2 años': 3,
  'Sin plazo': 4,
};

// El id de una etiqueta en un catálogo, sin distinguir mayúsculas; undefined si no la tiene.
function idDe(
  catalogo: Record<number, string>,
  etiqueta: string,
): number | undefined {
  const buscada = etiqueta.trim().toLocaleLowerCase('es');
  const encontrada = Object.entries(catalogo).find(
    ([, texto]) => texto.toLocaleLowerCase('es') === buscada,
  );
  return encontrada ? Number(encontrada[0]) : undefined;
}

/** El perfil de compra de la ficha como ids de catálogo, con las provincias que no corresponden a ninguna de Gescom. */
export function perfilDeLaFicha(
  plazo: unknown,
  facturacion: unknown,
  provincias: unknown,
): {
  plazo: number | null;
  tramos: number[];
  provincias: number[];
  sinEquivalente: string[];
} {
  const lista = (v: unknown) =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  const ids = (catalogo: Record<number, string>, etiquetas: string[]) =>
    Array.from(
      new Set(
        etiquetas
          .map((e) => idDe(catalogo, e))
          .filter((id): id is number => id !== undefined),
      ),
    ).sort((a, b) => a - b);
  return {
    plazo:
      typeof plazo === 'string'
        ? (PLAZO_DE_LA_FICHA[plazo.trim()] ?? null)
        : null,
    tramos: ids(TRAMOS, lista(facturacion)),
    provincias: ids(PROVINCIAS, lista(provincias)),
    sinEquivalente: lista(provincias).filter(
      (p) => idDe(PROVINCIAS, p) === undefined,
    ),
  };
}
